// ./src/ui/components/Login.tsx
import React, { useState } from 'react';
import { useAuth } from '@core/gameengine/state/auth';
import { useT } from '@core/i18n';
import { track, trackScreen } from '@core/analytics';

// ADDED: A utility function to unlock the global AudioContext.
// This should be called on the first user interaction (e.g., a click).
const unlockAudioContext = (() => {
  let unlocked = false;
  return () => {
    if (unlocked || typeof window === 'undefined') return;
    try {
      const context = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      if (context.state === 'suspended') {
        context.resume();
      }
      unlocked = true;
      console.log('Audio context unlocked by user interaction.');
    } catch (e) {
      console.error('Could not unlock audio context:', e);
    }
  };
})();

export const Login: React.FC = () => {
  const t = useT();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { login, isLoading, error, startDemo } = useAuth(state => ({
    login: state.login,
    isLoading: state.isLoading,
    error: state.error,
    startDemo: state.startDemo,
  }));

  React.useEffect(() => {
    trackScreen('login', 'Login');
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // CHANGED: Unlock audio on user interaction.
    unlockAudioContext();
    if (username && password) {
      track('login_attempt', { method: 'password' });
      login(username, password);
    }
  };

  const handleDemoClick = () => {
    // CHANGED: Unlock audio on user interaction.
    unlockAudioContext();
    track('demo_start');
    startDemo();
  };

  return (
    <div style={styles.container}>
      <form onSubmit={handleSubmit} style={styles.form}>
        <h2 style={styles.title}>{t('ui.lobby.welcome')}</h2>
        <div style={styles.inputGroup}>
          <label htmlFor="username" style={styles.label}>
            {t('ui.login.username')}
          </label>
          <input
            type="text"
            id="username"
            value={username}
            onChange={e => setUsername(e.target.value)}
            style={styles.input}
          />
        </div>
        <div style={styles.inputGroup}>
          <label htmlFor="password" style={styles.label}>
            {t('ui.login.password')}
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            style={styles.input}
          />
        </div>
        {error && <p style={styles.error}>{error}</p>}
        <button type="submit" style={styles.button} disabled={isLoading}>
          {isLoading ? t('ui.login.logging_in') : t('ui.login.login')}
        </button>
      </form>
      <button
        onClick={handleDemoClick}
        style={styles.demoButton}
        disabled={isLoading}
      >
        {isLoading ? t('ui.login.starting_demo') : t('ui.login.play_demo')}
      </button>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#101316',
    color: '#e8f0f8',
    fontFamily: "'Inter', system-ui, sans-serif",
    zIndex: 3000,
    pointerEvents: 'auto',
    flexDirection: 'column',
    gap: '20px',
  },
  form: {
    background: '#1c2228',
    padding: '32px',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
    width: '320px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  title: {
    textAlign: 'center',
    margin: '0 0 16px 0',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    marginBottom: '8px',
    fontSize: '14px',
    color: '#A0AEBB',
  },
  input: {
    padding: '10px',
    borderRadius: '6px',
    border: '1px solid #30363d',
    background: '#0d1117',
    color: '#e8f0f8',
    fontSize: '16px',
  },
  button: {
    padding: '12px',
    borderRadius: '6px',
    border: 'none',
    background: '#00e0ff',
    color: '#101316',
    fontWeight: 'bold',
    fontSize: '16px',
    cursor: 'pointer',
    marginTop: '8px',
  },
  error: {
    color: '#F44336',
    textAlign: 'center',
    margin: '0',
    fontSize: '14px',
  },
  demoButton: {
    padding: '12px 24px',
    borderRadius: '6px',
    border: '1px solid #00e0ff',
    background: 'transparent',
    color: '#00e0ff',
    fontWeight: 'bold',
    fontSize: '16px',
    cursor: 'pointer',
  },
};
